import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

interface DecorativeElement {
  type: string;
  imageUrl: string;
  position: string;
  size: string;
}

interface Theme {
  id: string;
  name: string;
  name_ar: string;
  colors: {
    primary?: string;
    secondary?: string;
    accent?: string;
    background?: string;
    foreground?: string;
    [key: string]: string | undefined;
  };
  decorative_elements: DecorativeElement[];
  is_active: boolean;
  valid_from: string;
  valid_until: string | null;
}

export const useActiveTheme = () => {
  const { data: activeTheme } = useQuery({
    queryKey: ["active-theme"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("themes")
        .select("*")
        .eq("is_active", true)
        .lte("valid_from", new Date().toISOString())
        .or(`valid_until.is.null,valid_until.gt.${new Date().toISOString()}`)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      
      if (data) {
        return {
          ...data,
          decorative_elements: (data.decorative_elements as unknown as DecorativeElement[]) || []
        } as Theme;
      }
      
      return null;
    },
    refetchInterval: 60000, // Check every minute
  });

  useEffect(() => {
    if (activeTheme && activeTheme.colors) {
      const root = document.documentElement;
      
      // Apply theme colors to CSS variables
      Object.entries(activeTheme.colors).forEach(([key, value]) => {
        if (value) {
          root.style.setProperty(`--${key}`, value);
        }
      });

      // Apply decorative elements
      const existingContainer = document.getElementById("theme-decorations");
      if (existingContainer) {
        existingContainer.remove();
      }

      if (activeTheme.decorative_elements && activeTheme.decorative_elements.length > 0) {
        const container = document.createElement("div");
        container.id = "theme-decorations";
        container.style.cssText = "position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999;";
        
        activeTheme.decorative_elements.forEach((element) => {
          const img = document.createElement("img");
          img.src = element.imageUrl;
          img.alt = element.type;
          img.style.cssText = `
            position: absolute;
            ${element.position === "top-right" ? "top: 20px; right: 20px;" : ""}
            ${element.position === "top-left" ? "top: 20px; left: 20px;" : ""}
            ${element.position === "bottom-right" ? "bottom: 20px; right: 20px;" : ""}
            ${element.position === "bottom-left" ? "bottom: 20px; left: 20px;" : ""}
            ${element.size === "small" ? "width: 80px; height: 80px;" : ""}
            ${element.size === "medium" ? "width: 120px; height: 120px;" : ""}
            ${element.size === "large" ? "width: 180px; height: 180px;" : ""}
            object-fit: contain;
            opacity: 0.9;
            animation: fadeIn 0.5s ease-in;
          `;
          container.appendChild(img);
        });
        
        document.body.appendChild(container);
      }
    } else {
      // Reset to default colors when no active theme
      const root = document.documentElement;
      
      // Get original values from the stylesheet
      const originalPrimary = "345 80% 35%";
      const originalSecondary = "0 0% 29%";
      const originalAccent = "345 70% 45%";
      
      root.style.setProperty("--primary", originalPrimary);
      root.style.setProperty("--secondary", originalSecondary);
      root.style.setProperty("--accent", originalAccent);

      // Remove decorations
      const existingContainer = document.getElementById("theme-decorations");
      if (existingContainer) {
        existingContainer.remove();
      }
    }
  }, [activeTheme]);

  return { activeTheme };
};
